/**
 * Editor page route
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { Button, cn } from '@frameit/ui';

// Check if Clerk is configured
const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
import {
  EditorCanvas,
  FrameSelector,
  MatSelector,
  PaperSelector,
  SceneSelector,
  EffectsPanel,
  GalleryWallEditor,
  MobileToolbar,
  MobilePanelSheet,
  FloatingArtworkButton,
  MobileArtworksSheet,
  ImageTransformPanel,
} from '../features/editor';
import { ComparisonView } from '../features/editor/ComparisonView';
import { ShareDialog } from '../features/share/ShareDialog';
import { ARViewer } from '../features/ar/ARViewer';
import { isARSupported } from '../features/ar/ar-support';
import { Uploader } from '../features/upload/Uploader';
import { ExportPanel } from '../features/export/ExportPanel';
import {
  useProjectStore,
  getLocalImageUrl,
  setRemoteImageUrl,
  type EditorMode,
} from '../stores/projectStore';
import { useArtworkUpload } from '../hooks/useArtworkUpload';
import * as api from '../lib/api-client';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSyncStatus } from '../hooks/useSyncStatus';

// Check if Ctrl/Cmd key is pressed
const isMac =
  typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

/** Editor mode configuration */
const EDITOR_MODES: { id: EditorMode; label: string; icon: string }[] = [
  {
    id: 'frame',
    label: 'Frame',
    icon: 'M4 3h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1zm1 2v14h14V5H5z',
  },
  { id: 'mat', label: 'Mat', icon: 'M4 4h16v16H4V4zm2 2v12h12V6H6z' },
  {
    id: 'image',
    label: 'Image',
    icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    id: 'paper',
    label: 'Paper',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    id: 'effects',
    label: 'Effects',
    icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  },
  {
    id: 'scene',
    label: 'Scene',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
  {
    id: 'gallery',
    label: 'Gallery',
    icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  },
  {
    id: 'export',
    label: 'Export',
    icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  },
];

/** Editor page component */
export function EditorPage() {
  const { projectId } = useParams();
  const project = useProjectStore((s) => s.project);
  const editorMode = useProjectStore((s) => s.editorMode);
  const setEditorMode = useProjectStore((s) => s.setEditorMode);
  const addArtwork = useProjectStore((s) => s.addArtwork);
  const createProject = useProjectStore((s) => s.createProject);
  const isDirty = useProjectStore((s) => s.isDirty);
  const selectedArtworkId = useProjectStore((s) => s.selectedArtworkId);
  const selectArtwork = useProjectStore((s) => s.selectArtwork);
  const removeArtwork = useProjectStore((s) => s.removeArtwork);
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const canUndo = useProjectStore((s) => s.canUndo);
  const canRedo = useProjectStore((s) => s.canRedo);
  const saveToHistory = useProjectStore((s) => s.saveToHistory);
  const canvasZoom = useProjectStore((s) => s.canvasZoom);
  const setCanvasZoom = useProjectStore((s) => s.setCanvasZoom);
  const zoomToFit = useProjectStore((s) => s.zoomToFit);
  const showComparison = useProjectStore((s) => s.showComparison);
  const toggleComparison = useProjectStore((s) => s.toggleComparison);
  // Upload hook & sync status
  const { upload: uploadToApi } = useArtworkUpload();
  const { status: syncStatus } = useSyncStatus();
  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  // AR viewer state
  const [showAR, setShowAR] = useState(false);
  // Mobile detection
  const isMobile = useIsMobile();
  // Artworks sidebar visible only on lg+ (1024px); show floating button below that
  const [showArtworksSidebar, setShowArtworksSidebar] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList): void =>
      setShowArtworksSidebar(e.matches);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const loadProject = useProjectStore((s) => s.loadProject);
  const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  // Initialize project — load from API or create new
  useEffect(() => {
    if (projectId && !isDevMode) {
      // Load project from API
      api.getProject(projectId).then((p) => {
        loadProject(p);
        // Register remote image URLs for artworks
        for (const artwork of p.framedArtworks) {
          setRemoteImageUrl(
            artwork.artworkId,
            api.getArtworkImageUrl(artwork.artworkId, 'original'),
          );
        }
      });
    } else if (!project) {
      createProject('Untitled Project');
    }
  }, [projectId, isDevMode, loadProject, createProject, project]);

  // Handle artwork upload — local preview + background API sync
  const handleUpload = (file: File, dimensions: { width: number; height: number }): void => {
    saveToHistory();

    // Start upload (local blob for instant preview, API in background)
    uploadToApi(file, dimensions).then((result) => {
      addArtwork(result.artworkId, {
        templateId: 'frame-001',
        width: 25,
      });
    });
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = isMac ? e.metaKey : e.ctrlKey;
      if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (isMod && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="h-14 border-b flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <h1 className="font-semibold">FrameIt</h1>
          <span className="text-sm text-muted-foreground truncate max-w-[120px] sm:max-w-none">
            {project?.name || 'Loading...'}
            {isDirty && ' *'}
          </span>
          {syncStatus === 'saving' && (
            <span className="text-xs text-muted-foreground animate-pulse">Saving...</span>
          )}
          {syncStatus === 'saved' && <span className="text-xs text-green-600">Saved</span>}
          {syncStatus === 'offline' && <span className="text-xs text-yellow-600">Offline</span>}
          {syncStatus === 'error' && <span className="text-xs text-destructive">Sync failed</span>}
        </div>

        <div className="flex items-center gap-2">
          {isARSupported() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAR(true)}
              disabled={!selectedArtworkId}
              title="View on your wall (AR)"
            >
              <svg
                className="w-4 h-4 sm:mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span className="hidden sm:inline">AR</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowShareDialog(true)}
            disabled={!project || !selectedArtworkId}
          >
            <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <span className="hidden sm:inline">Share</span>
          </Button>
          <Link to="/projects">
            <Button variant="ghost" size="sm">
              Projects
            </Button>
          </Link>
          {hasClerk ? (
            <UserButton afterSignOutUrl="/auth/sign-in" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
              D
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Mode tabs (vertical) - Desktop only */}
        <aside className="hidden md:flex w-16 border-r flex-col items-center py-2 gap-0.5 bg-muted/30">
          {EDITOR_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setEditorMode(mode.id)}
              className={cn(
                'w-14 h-14 rounded-lg flex flex-col items-center justify-center gap-0.5',
                'transition-colors',
                editorMode === mode.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground',
              )}
              title={mode.label}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d={mode.icon}
                />
              </svg>
              <span className="text-[10px] leading-none font-medium">{mode.label}</span>
            </button>
          ))}
        </aside>

        {/* Left panel - Controls - Desktop only */}
        <aside className="hidden md:flex w-80 border-r flex-col overflow-hidden">
          {editorMode === 'frame' && <FrameSelector />}
          {editorMode === 'mat' && <MatSelector />}
          {editorMode === 'image' && <ImageTransformPanel />}
          {editorMode === 'paper' && <PaperSelector />}
          {editorMode === 'effects' && <EffectsPanel />}
          {editorMode === 'scene' && <SceneSelector />}
          {editorMode === 'gallery' && <GalleryWallEditor />}
          {editorMode === 'export' && <ExportPanel />}
        </aside>

        {/* Main canvas area */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Canvas container */}
          <div className="flex-1 relative p-2 md:p-4 bg-muted/20">
            <EditorCanvas className="absolute inset-2 md:inset-4" />

            {/* Side-by-side comparison overlay */}
            {showComparison && selectedArtworkId && (
              <ComparisonView onClose={toggleComparison} className="inset-2 md:inset-4" />
            )}
          </div>

          {/* Bottom toolbar - Desktop only */}
          <div className="hidden md:flex h-10 items-center justify-center gap-2 border-t bg-background px-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={undo}
              disabled={!canUndo()}
              title={isMac ? '⌘Z' : 'Ctrl+Z'}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a4 4 0 014 4v2M3 10l4-4m-4 4l4 4"
                />
              </svg>
              Undo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={redo}
              disabled={!canRedo()}
              title={isMac ? '⌘⇧Z' : 'Ctrl+Y'}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 10H11a4 4 0 00-4 4v2m14-6l-4-4m4 4l-4 4"
                />
              </svg>
              Redo
            </Button>
            <div className="w-px h-5 bg-border mx-1" />
            <Button
              variant={showComparison ? 'secondary' : 'ghost'}
              size="sm"
              onClick={toggleComparison}
              disabled={!selectedArtworkId}
              title="Before/After comparison"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 8h6m-6 4h6m-6 4h6M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18" />
              </svg>
              Compare
            </Button>
            <div className="w-px h-5 bg-border mx-1" />
            <Button variant="ghost" size="sm" onClick={zoomToFit}>
              Fit
            </Button>
            <Button
              variant={canvasZoom === 100 ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setCanvasZoom(100)}
            >
              100%
            </Button>
          </div>

          {/* Mobile toolbar */}
          {isMobile && <MobileToolbar />}
        </main>

        {/* Right panel - Artwork list & upload - Desktop/large tablet only */}
        <aside className="hidden lg:flex w-64 border-l flex-col">
          <div className="p-4 border-b">
            <h3 className="font-medium mb-2">Artworks</h3>
            <Uploader onUpload={handleUpload} allowCamera />
          </div>

          {/* Artwork thumbnails */}
          <div className="flex-1 overflow-auto p-4 space-y-2">
            {project?.framedArtworks.map((artwork) => {
              const imageUrl = getLocalImageUrl(artwork.artworkId);
              const isSelected = selectedArtworkId === artwork.id;

              return (
                <div
                  key={artwork.id}
                  className={cn(
                    'relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all',
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-transparent hover:border-muted-foreground/50',
                  )}
                  onClick={() => selectArtwork(artwork.id)}
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt="Artwork" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">No image</span>
                    </div>
                  )}

                  {/* Delete button */}
                  <button
                    className="absolute top-1 right-1 w-6 h-6 bg-black/50 hover:bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      saveToHistory();
                      removeArtwork(artwork.id);
                    }}
                    style={{ opacity: isSelected ? 1 : undefined }}
                  >
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-[10px] rounded">
                      Selected
                    </div>
                  )}
                </div>
              );
            })}

            {/* Empty state */}
            {(!project?.framedArtworks || project.framedArtworks.length === 0) && (
              <div className="text-center text-sm text-muted-foreground py-8">
                Upload an image to get started
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile-only components (controls sheet + toolbar) */}
      {isMobile && <MobilePanelSheet />}

      {/* Floating artwork access — visible when right sidebar is hidden (<1024px) */}
      {!showArtworksSidebar && (
        <>
          <FloatingArtworkButton />
          <MobileArtworksSheet />
        </>
      )}

      {/* Share dialog */}
      <ShareDialog open={showShareDialog} onOpenChange={setShowShareDialog} />

      {/* AR Viewer */}
      {showAR && <ARViewer onClose={() => setShowAR(false)} />}
    </div>
  );
}
